import { useState } from "react";
import axios from "axios";
import { Phone, Mail, Clock, MapPin, ShieldCheck, Loader2, ArrowRight, Send } from "lucide-react";
import { toast } from "sonner";
import { SITE, IMG } from "@/lib/site";
import Chapter from "@/components/cinema/Chapter";
import { Reveal, LineReveal } from "@/components/cinema/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "", consent: false });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent) { toast.error("Please acknowledge the privacy notice."); return; }
    if (!form.name || !form.email || !form.message) { toast.error("Please complete the required fields."); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      toast.success("Message received. We will be in touch shortly.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "", consent: false });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please call us directly.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contact" className="bg-[hsl(var(--gaza-teal-deep))] text-white">
      <Chapter img={IMG.contact} overlay="left" testId="contact-header">
        <div className="grid md:grid-cols-12 gap-12 items-end">
          <div className="md:col-span-7">
            <Reveal y={16}><div className="eyebrow eyebrow-light mb-6">Get in touch</div></Reveal>
            <LineReveal as="h1" className="display-serif text-4xl sm:text-5xl leading-[0.95]" lines={["One call sets", "a calm, professional", "recovery in motion."]} />
          </div>
          <Reveal delay={0.45} className="md:col-span-5 text-white/80 leading-relaxed text-lg">
            From emergency response and contents protection to storage, moving and final pack-back, BackHome Restoration Inc. provides comprehensive solutions designed to make recovery easier.
          </Reveal>
        </div>
      </Chapter>

      <Chapter img={IMG.torontoBlueHour} overlay="dark" testId="contact-body" innerClassName="grid md:grid-cols-12 gap-12">
        {/* Left — details */}
        <Reveal className="md:col-span-5 space-y-10">
          <a href={SITE.phoneHref} className="block group" data-testid="contact-phone-card">
            <div className="eyebrow eyebrow-light mb-3">24/7 Emergency Line</div>
            <div className="display-serif text-4xl md:text-5xl text-white group-hover:text-[hsl(var(--gaza-blue-bright))] transition flex items-center gap-3">
              <Phone className="w-7 h-7" /> {SITE.phone}
            </div>
            <div className="text-sm text-white/65 mt-2">Tap to call. Coordinators respond around the clock.</div>
          </a>

          <div className="space-y-6 pt-2">
            <div className="flex gap-4" data-testid="contact-hours">
              <div className="w-11 h-11 border border-white/20 bg-white/5 grid place-items-center shrink-0"><Clock className="w-5 h-5 text-[hsl(var(--gaza-blue-bright))]" /></div>
              <div>
                <div className="font-semibold text-white">Always Available</div>
                <div className="text-white/65 text-sm">24 hours · 7 days a week · every day of the year</div>
              </div>
            </div>
            <div className="flex gap-4" data-testid="contact-area">
              <div className="w-11 h-11 border border-white/20 bg-white/5 grid place-items-center shrink-0"><MapPin className="w-5 h-5 text-[hsl(var(--gaza-blue-bright))]" /></div>
              <div>
                <div className="font-semibold text-white">Service Area</div>
                <div className="text-white/65 text-sm">Toronto, Mississauga, Brampton, Vaughan, Markham, Richmond Hill, Ajax, Pickering, Whitby, Oshawa, Burlington, Oakville and surrounding communities.</div>
              </div>
            </div>
            <div className="flex gap-4" data-testid="contact-privacy-note">
              <div className="w-11 h-11 border border-white/20 bg-white/5 grid place-items-center shrink-0"><ShieldCheck className="w-5 h-5 text-[hsl(var(--gaza-blue-bright))]" /></div>
              <div>
                <div className="font-semibold text-white">Your information is protected</div>
                <div className="text-white/65 text-sm">We only use the details you provide to respond to your inquiry. We do not share or sell your information.</div>
              </div>
            </div>
          </div>

          <a href={SITE.emailHref} className="block group" data-testid="contact-email-card">
            <div className="eyebrow eyebrow-light mb-3">Claims & Enquiries</div>
            <div className="display-serif text-2xl md:text-3xl text-white group-hover:text-[hsl(var(--gaza-blue-bright))] transition flex items-center gap-3 break-all">
              <Mail className="w-6 h-6 shrink-0" /> {SITE.email}
            </div>
            <div className="text-sm text-white/65 mt-2">Email documentation, claim details or general questions.</div>
          </a>
        </Reveal>

        {/* Right — form */}
        <Reveal delay={0.2} className="md:col-span-7">
          <div className="glass-panel p-8 md:p-12" data-testid="contact-form-wrap">
            <div className="eyebrow mb-3">Send a message</div>
            <h2 className="display-serif text-3xl md:text-4xl text-[hsl(var(--gaza-navy))] leading-tight mb-8">Tell us how we can help.</h2>

            {submitted ? (
              <div className="py-10 text-center" data-testid="contact-success">
                <div className="w-14 h-14 mx-auto rounded-full bg-[hsl(var(--gaza-gold))] text-[hsl(var(--gaza-navy))] grid place-items-center display-serif text-2xl">✓</div>
                <h3 className="display-serif text-3xl text-[hsl(var(--gaza-navy))] mt-6">Thank you.</h3>
                <p className="text-[hsl(var(--gaza-muted))] mt-3 max-w-md mx-auto">Your message has been received. A coordinator will follow up shortly. For urgent matters, please call <a href={SITE.phoneHref} className="font-semibold text-[hsl(var(--gaza-navy))] underline">{SITE.phone}</a>.</p>
                <button onClick={() => setSubmitted(false)} className="mt-8 btn-primary" data-testid="contact-send-another">Send another message <ArrowRight className="w-4 h-4" /></button>
              </div>
            ) : (
              <form onSubmit={onSubmit} noValidate className="space-y-5" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Full name *" name="name">
                    <input data-testid="contact-input-name" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Jane Doe" />
                  </Field>
                  <Field label="Email *" name="email">
                    <input data-testid="contact-input-email" required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="jane@example.com" />
                  </Field>
                  <Field label="Phone" name="phone">
                    <input data-testid="contact-input-phone" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="(416) 555-0144" />
                  </Field>
                  <Field label="Subject" name="subject">
                    <input data-testid="contact-input-subject" value={form.subject} onChange={set("subject")} className={inputCls} placeholder="Water damage, claim, etc." />
                  </Field>
                </div>
                <Field label="Message *" name="message">
                  <textarea data-testid="contact-input-message" required rows={6} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} placeholder="Briefly describe what happened, the property type and any timing constraints." />
                </Field>

                <label className="flex items-start gap-3 text-sm text-[hsl(var(--gaza-ink))] cursor-pointer select-none" data-testid="contact-consent">
                  <input type="checkbox" checked={form.consent} onChange={set("consent")} className="mt-1 w-4 h-4 accent-[hsl(var(--gaza-gold))]" required />
                  <span>I have read and understand the privacy notice. I consent to BackHome Restoration Inc. using the information above solely to respond to my inquiry.</span>
                </label>

                <button type="submit" disabled={submitting} className="btn-primary w-full sm:w-auto" data-testid="contact-submit-btn">
                  {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <><Send className="w-4 h-4" /> Send Message</>}
                </button>
                <div className="text-xs text-[hsl(var(--gaza-muted))] mt-2">Need immediate help? Call <a href={SITE.phoneHref} className="underline font-semibold">{SITE.phone}</a>.</div>
              </form>
            )}
          </div>
        </Reveal>
      </Chapter>
    </div>
  );
}

const inputCls = "w-full px-4 py-3.5 bg-[hsl(var(--gaza-paper))] border border-[hsl(var(--gaza-line))] focus:border-[hsl(var(--gaza-gold))] focus:ring-1 focus:ring-[hsl(var(--gaza-gold))] outline-none transition text-[hsl(var(--gaza-navy))] placeholder:text-[hsl(var(--gaza-muted))]/70 text-sm";

function Field({ label, name, children }) {
  return (
    <label className="block" htmlFor={name}>
      <span className="block text-[11px] uppercase tracking-[0.22em] font-semibold text-[hsl(var(--gaza-navy))] mb-2">{label}</span>
      {children}
    </label>
  );
}
